import {
  FolderGit2,
  InfinityIcon,
  MessagesSquare,
  Search,
  Waypoints,
  type LucideIcon,
} from "lucide-react"
import type { ReactNode } from "react"

// Keep the static server-rendered network chart styled before its JS hydrates.
import "./illustrations/network-chart.css"
import { CascadeReveal } from "./CascadeReveal"
import { deferUntilVisible } from "./deferUntilVisible"
import type { HomeNewPanelArt } from "./HomeNewPanelBackdrop"
import { TrailsShowcase } from "./HomeNewTrailsCard"
import { TrailsIcon } from "./TrailsIcon"

const ExpandedCheckpointTimeline = deferUntilVisible(async () => ({
  default: (await import("@/routes/features/sessions/-components/SessionsFeaturesSection"))
    .ExpandedCheckpointTimeline,
}))

const SessionsPrivacyIllustration = deferUntilVisible(async () => ({
  default: (await import("../home/SessionsSyncSection")).SessionsPrivacyIllustration,
}))

const CommitMirrorShowcase = deferUntilVisible(async () => ({
  default: (await import("./CommitMirrorShowcase")).CommitMirrorShowcase,
}))

const SessionLinksShowcase = deferUntilVisible(async () => ({
  default: (await import("./SessionLinksShowcase")).SessionLinksShowcase,
}))

const CodeAndContextIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/AgenticSearchIllustrations")).CodeAndContextIllustration,
}))

const FewerStepsIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/AgenticSearchIllustrations")).FewerStepsIllustration,
}))

const CiChecksIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/CiIllustrations")).CiChecksIllustration,
}))

const CiPartnersIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/CiIllustrations")).CiPartnersIllustration,
}))

const AgentReviewFindingsIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/CodeHostingTrailsIllustrations"))
    .AgentReviewFindingsIllustration,
}))

const FastestGitNetworkIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/CodeHostingTrailsIllustrations"))
    .FastestGitNetworkIllustration,
}))

const IdeaToTrailIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/CodeHostingTrailsIllustrations")).IdeaToTrailIllustration,
}))

const PreferredRegionsIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/CodeHostingTrailsIllustrations"))
    .PreferredRegionsIllustration,
}))

const BrainSourcesIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/ContinuousMemoryIllustrations")).BrainSourcesIllustration,
}))

const TeamBrainIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/ContinuousMemoryIllustrations")).TeamBrainIllustration,
}))

const ContextRetrievalIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/SemanticGraphIllustrations"))
    .ContextRetrievalIllustration,
}))

const TokenFieldIllustration = deferUntilVisible(async () => ({
  default: (await import("./illustrations/SemanticGraphIllustrations")).TokenFieldIllustration,
}))

export type HomeNewProductId =
  | "code-hosting"
  | "session-storage"
  | "trails"
  | "continuous-memory"
  | "agentic-search"
  | "ci"

export interface HomeNewProductClaim {
  title: string
  description: ReactNode
  /** Rendered in the claim's visual slot. */
  visual: ReactNode
  /** Reserve taller artwork even on client navigation, before its code loads. */
  visualMinHeight?: number
}

export type HomeNewProduct = {
  id: HomeNewProductId
  Icon: LucideIcon | typeof TrailsIcon
  /** Sticky menu entry. */
  nav: { label: string; description: string }
  /** Section intro: the bold lead sentence and the muted continuation. */
  intro: {
    lead: string
    rest: string
    /** Runs the continuation on from the lead instead of starting a new line. */
    runOn?: boolean
  }
  /** Where the section's "Learn more" ghost button leads. */
  cta?: { href: string }
  /** Two claims side by side, or four in two rows. */
  claims:
    | [HomeNewProductClaim, HomeNewProductClaim]
    | [HomeNewProductClaim, HomeNewProductClaim, HomeNewProductClaim, HomeNewProductClaim]
} & (
  | {
      /** Landscape behind the section's visual panel. */
      backdrop: { art: HomeNewPanelArt }
      /** Animated scene laid over the backdrop panel. */
      showcase: ReactNode
    }
  // Without a backdrop the section has no panel.
  | { backdrop?: never; showcase?: never }
)

export const homeNewProducts: HomeNewProduct[] = [
  {
    id: "code-hosting",
    Icon: FolderGit2,
    backdrop: { art: "cliffs" },
    nav: { label: "Code Hosting", description: "Host code on a distributed network" },
    intro: {
      lead: "Distributed by design. Fast by default.",
      rest: "Code Hosting that builds on Git’s decentralized roots.",
    },
    cta: { href: "/features/code-hosting" },
    showcase: <CommitMirrorShowcase />,
    claims: [
      {
        title: "The fastest Git network on Earth",
        description: (
          <>
            Let agents clone and push to your repositories on a new Git-compatible source control
            server. Measured with{" "}
            <a
              href="https://github.com/entireio/forgemark"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-current underline-offset-4 hover:text-default focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              forgemark
            </a>
            , our open source Git benchmark.
          </>
        ),
        visual: <FastestGitNetworkIllustration />,
      },
      {
        title: "Host code in your preferred regions",
        description:
          "Host your code where your team is. Available today in India, Australia, EU and the US.",
        visual: <PreferredRegionsIllustration />,
      },
    ],
  },
  {
    id: "trails",
    Icon: TrailsIcon,
    backdrop: { art: "canyon" },
    nav: { label: "Trails", description: "Take work from idea to merge" },
    intro: {
      lead: "Take work from idea to merge.",
      rest: "Replace the PR with Trails. Let your agents loop until evals are green. View changes across multiple repos. Trace your code to the session that produced it.",
    },
    cta: { href: "/features/trails" },
    showcase: <TrailsShowcase />,
    claims: [
      {
        title: "From idea to implementation",
        description:
          "Write the idea down first, or start from an agent’s push. The goal stays with the work until it merges.",
        visual: <IdeaToTrailIllustration />,
      },
      {
        title: "Catch what you missed",
        description:
          "Agent reviews and evals surface actionable findings with code locations and context.",
        visual: <AgentReviewFindingsIllustration />,
      },
    ],
  },
  {
    id: "session-storage",
    Icon: MessagesSquare,
    backdrop: { art: "icebergs" },
    nav: { label: "Session Storage", description: "Understand the why behind each change" },
    intro: {
      lead: "Never start from zero.",
      rest: "Give your next session the history of how your codebase was built, not just the code that survived.",
    },
    cta: { href: "/features/sessions" },
    showcase: <SessionLinksShowcase />,
    claims: [
      {
        title: "Trace how a change happened",
        description:
          "Open a checkpoint to revisit the prompts, tool calls, and decisions that shaped the code.",
        visual: (
          <CascadeReveal>
            <ExpandedCheckpointTimeline
              copy={{
                authorUsername: "evisdren",
                prompt: "When did upload retries break?",
                meta: "1d ago",
                toolCalls: ["Searched 2w of checkpoints", "Bisected the upload changes"],
                reply: (
                  <>
                    It regressed in <span className="font-mono text-xs">a3f9c21</span>, when the
                    upload logic was condensed and the backoff dropped.
                  </>
                ),
              }}
            />
          </CascadeReveal>
        ),
      },
      {
        title: "Protected by design",
        description:
          "Entire detects and redacts secrets before anything is stored. Sensitive data never leaves your machine.",
        visual: (
          <CascadeReveal>
            <SessionsPrivacyIllustration compact authorUsername="matthiaswenz" />
          </CascadeReveal>
        ),
      },
    ],
  },
  {
    id: "continuous-memory",
    Icon: Waypoints,
    nav: { label: "Continuous Memory", description: "Turn your repo into a multi-agent brain" },
    intro: {
      lead: "The repo is the only memory your agent should need.",
      rest: "Combine your prompts, model responses, and tool calls with a code graph to turn your repo into a continuously updated multi-agent brain.",
      runOn: true,
    },
    claims: [
      {
        title: "Retrieve context, not whole repositories",
        description:
          "Agents query the graph for relevant files, symbols, and decisions, reducing unnecessary reads and token consumption.",
        visual: <ContextRetrievalIllustration />,
      },
      {
        title: "Up to 71% fewer tokens",
        description: (
          <>
            Reduce token usage by up to 71% while outperforming Graphify on retrieval{" "}
            <a
              href="https://github.com/entireio/entire-graph#benchmarks"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-current underline-offset-4 hover:text-default focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              benchmarks
            </a>
            .
          </>
        ),
        visual: <TokenFieldIllustration />,
      },
      {
        title: "Agents get smarter with every session",
        description:
          "entire-brain joins captured sessions with facts, docs, and code understanding from entire-graph, so agents start informed.",
        visual: <BrainSourcesIllustration />,
      },
      {
        title: "One brain for the whole team",
        description:
          "Connect entire-brain to a hosted server and your team’s agent memories live in one shared space, not scattered across individual machines.",
        visual: <TeamBrainIllustration />,
      },
    ],
  },
  {
    id: "agentic-search",
    Icon: Search,
    nav: { label: "Agentic Search", description: "Search code and context in one query" },
    intro: {
      lead: "Find the why in one query.",
      rest: "Search code, commits, and agent sessions across every repository your agent can reach, with the reasoning attached.",
    },
    cta: { href: "/blog/introducing-agentic-search-for-code-and-context" },
    claims: [
      {
        title: "Code and context together",
        description:
          "Exact, symbol-aware code search beside semantic search over sessions and commits; the agent picks the engine per query.",
        visual: <CodeAndContextIllustration />,
      },
      {
        title: "Fewer steps, fewer tokens",
        description: (
          <>
            In our{" "}
            <a
              href="/blog/introducing-agentic-search-for-code-and-context"
              className="underline decoration-current underline-offset-4 hover:text-default focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              benchmarks
            </a>
            , agents with search answered 81 of 90 history questions instead of 70, using under half
            the tokens and steps.
          </>
        ),
        visual: <FewerStepsIllustration />,
      },
    ],
  },
  {
    id: "ci",
    Icon: InfinityIcon,
    nav: { label: "Continuous Integration", description: "Pipelines that keep up with agents" },
    intro: {
      lead: "CI built for agent-scale.",
      rest: "Integrations with leading partners so your build and test pipelines keep up with how fast agents ship.",
    },
    claims: [
      {
        title: "Checks at agent speed",
        description:
          "Builds and tests start the moment an agent pushes, so every change gets checked without waiting in a queue.",
        visual: <CiChecksIllustration />,
      },
      {
        title: "Native partner integrations",
        description:
          "Connect leading agent-scale CI providers in a few clicks, with results reported next to the code and sessions they checked.",
        visual: <CiPartnersIllustration />,
        visualMinHeight: 260,
      },
    ],
  },
]

export const homeNewProductIds = homeNewProducts.map((product) => product.id)
